import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    inject,
    input,
    OnInit,
    OnDestroy,
    effect,
    signal,
    Signal,
    ViewChild,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiQuizEditorStoreService } from '../../fi-quiz-editor-store.service';
import { AppTextService } from '@fi-shared/app-text';
import { IAccessRightSelectionFiltersCollection } from '@fi-shared/models';
import { ISharingSettingsContentConfig } from '@fi-app/shared/access-right-filters/fi-sharing-settings-content/fi-sharing-settings-content.component';
import { ModuleType } from '@fi-app/shared/types';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FiQuizEditorTabIndex } from '../../../fi-quiz-enums';
import { AccessRightFilterCollectionModel } from '@fi-app/shared/models/access-right-filter-collection.model';
import { FiSharingSettingsContentComponent } from '@fi-app/shared/access-right-filters/fi-sharing-settings-content/fi-sharing-settings-content.component';
export interface IQuizSharingSettings {
    AccessRightFilterCollection: IAccessRightFilterCollection;
    IsShared: boolean;
}

@Component({
    selector: 'fi-quiz-sharing-settings',
    templateUrl: './quiz-sharing-settings.component.html',
    styleUrls: ['./quiz-sharing-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-sharing-settings' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiQuizSharingSettingsComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiQuizSharingSettingsComponent,
            multi: true,
        },
    ],
})
export class FiQuizSharingSettingsComponent
    extends ControlValueAccessorValidatorBase<IQuizSharingSettings>
    implements OnInit, OnDestroy
{
    private appTextService = inject(AppTextService);
    private fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    public scrollableElementRefs = input<ElementRef<HTMLElement>[]>();

    protected savedQuickSelectionFilters: Signal<IAccessRightSelectionFiltersCollection[]> =
        this.fiQuizEditorStoreService.savedSelectionFilters;
    protected selectedTabIndex: Signal<FiQuizEditorTabIndex> = this.fiQuizEditorStoreService.selectedTabIndex;
    protected selectedFiltersCollection: Signal<IAccessRightSelectionFiltersCollection> =
        this.fiQuizEditorStoreService.selectedFiltersCollection;
    protected availableAccessRightFilterCollection: Signal<AccessRightFilterCollectionModel> =
        this.fiQuizEditorStoreService.accessRightFilterCollection;

    private _formSubmittedEffect = effect(() => {
        const isFormSubmitted = this.fiQuizEditorStoreService.formFailedValidation();
        this.onInvalidStatusUpdate();
    });
    private _tabIndexChangeEffect = effect(() => {
        const tabIndex = this.selectedTabIndex();
        this.onInvalidStatusUpdate();
    });

    @ViewChild('fiSharingSettingsContentComponent')
    protected fiSharingSettingsContentComponent: FiSharingSettingsContentComponent;

    protected showErrorTooltip$ = new BehaviorSubject<boolean>(false);

    private _quizSharingSettingsSaveTriggerEffect = effect(() => {
        const quizSharingSettingsSaveTrigger = this.fiQuizEditorStoreService.onQuizSharingSettingsSaveTrigger();
        if (!quizSharingSettingsSaveTrigger) {
            return;
        }
        this.fiSharingSettingsContentComponent.saveNewQuickSelection().then(() => {
            this.fiQuizEditorStoreService.setIsQuizSharingSettingsSaved(true);
        });
    });

    protected isShared = signal<boolean>(false);
    protected sharingSettingsConfig = computed<ISharingSettingsContentConfig>(() => ({
        availableAccessRightFilterCollection: this.availableAccessRightFilterCollection(),
        moduleType: ModuleType.Onboarding,
        businessUnitIdsOutOfScope: [],
        isItemCreator: true,
        isRolesRequired: this.isShared(),
        showRolesSelect: false,
        showRolesCheckboxes: true,
        accessRightUserFilter: this.ngModel.AccessRightFilterCollection.AccessRightUserFilter,
        areasSectionHeaderText: this.appTextService.get('access_rights.manual_selection'),
        individualUnitSectionHeaderText: this.appTextService.get('sharing.individual_units'),
        individualUnitSectionDescriptionText: this.appTextService.get('quiz.access_right_locations_description'),
        quickSelectLabelId: 'access_rights.share_with',
        showErrorTooltip$: this.showErrorTooltip$.asObservable(),
        scrollableElementRefs: this.scrollableElementRefs(),
    }));

    public ngOnInit(): void {
        this.isShared.set(this.ngModel.IsShared);
        setTimeout(() => {
            this.subscribeOnControlStatusChange();
        }, 0);
    }

    private subscribeOnControlStatusChange() {
        this.controlComponent.statusChanges.pipe(takeUntil(this._ngOnDestroy$)).subscribe((status) => {
            this.onInvalidStatusUpdate();
        });
    }

    private onInvalidStatusUpdate(): void {
        if (!this.fiQuizEditorStoreService.formFailedValidation()) {
            this.showErrorTooltip$.next(false);
            return;
        }
        if (this.selectedTabIndex() === FiQuizEditorTabIndex.SettingsSharing) {
            this.setShowErrorTooltip();
            return;
        }
        this.showErrorTooltip$.next(false);
    }

    private setShowErrorTooltip(): void {
        const errors = this.controlComponent.errors || {};
        const missingSelectionErrors =
            errors.hasOwnProperty('missing-areas') ||
            errors.hasOwnProperty('missing-business-areas') ||
            errors.hasOwnProperty('missing-roles');
        this.showErrorTooltip$.next(missingSelectionErrors);
    }

    protected onSelectedFiltersCollectionChange(collection: IAccessRightSelectionFiltersCollection): void {
        this.fiQuizEditorStoreService.setSelectedFiltersCollection(collection);
    }

    protected onSavedQuickSelectionFiltersChange(
        newSavedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[],
    ): void {
        this.fiQuizEditorStoreService.setSavedSelectionFilters(newSavedQuickSelectionFilters);
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        const accessRightCoreFilter = this.ngModel.AccessRightFilterCollection.AccessRightCoreFilter;
        if (!this.ngModel.IsShared) {
            return {};
        }

        const isAreasRequired = !accessRightCoreFilter.BusinessUnitIds?.length;
        const hasAreas = !!accessRightCoreFilter.AreaIds?.length;
        const hasBusinessAreas = !!accessRightCoreFilter.BusinessAreaIds?.length;
        const hasRoles = !!accessRightCoreFilter.RoleIds?.length;

        const missingAreas = (isAreasRequired && !hasAreas) || (!isAreasRequired && hasBusinessAreas && !hasAreas);
        const missingBusinessAreas =
            (isAreasRequired && !hasBusinessAreas) || (!isAreasRequired && hasAreas && !hasBusinessAreas);

        return {
            'missing-areas': missingAreas,
            'missing-business-areas': missingBusinessAreas,
            'missing-roles': !hasRoles,
        };
    }

    protected onIsSharedChange(): void {
        this.isShared.set(this.ngModel.IsShared);
        this.doPropagateChanges();
    }

    protected readonly sharingSelectionOptions = [
        {
            label: this.appTextService.get('task.private'),
            value: false,
        },
        {
            label: this.appTextService.get('common.public'),
            value: true,
        },
    ];

    private _ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this._ngOnDestroy$.next();
        this._ngOnDestroy$.complete();
    }
}
